import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Tag,
  Space,
  Row,
  Col,
  Dropdown,
  Menu,
  message,
  Popconfirm,
  Tooltip,
  Badge,
  Typography,
  Empty,
  Spin,
  Skeleton
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  UserOutlined,
  CalendarOutlined,
  CommentOutlined,
  TagOutlined,
  ReloadOutlined,
  EyeOutlined,
  FilterOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";
import { TaskStatus, TaskPriority, ITask, CreateTaskRequest, UpdateTaskRequest } from "../../types/task.type";
import { taskApi } from "../../api/taskApi";
import TaskDetailsModal from "../../components/TaskDetailsModal/taskDetailsModal.component";
import "./tasks.page.css";
import { api } from "../../api/api";

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

interface User {
  _id: string;
  name: string;
  username: string;
  role: UserRole;
}

const TasksPage: React.FC = () => {
  const { authState } = useAuth();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState<ITask | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<TaskStatus | "all">("all");
  const [selectedPriority, setSelectedPriority] = useState<TaskPriority | "all">("all");
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState<ITask[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [form] = Form.useForm();

  // Модальное окно деталей
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState<ITask | null>(null);

  const isAdmin = authState.user?.role === UserRole.Admin;

  const priorityColors = {
    [TaskPriority.Low]: "blue",
    [TaskPriority.Medium]: "orange",
    [TaskPriority.High]: "red",
    [TaskPriority.Urgent]: "purple"
  };

  const statusColors = {
    [TaskStatus.Pending]: "default",
    [TaskStatus.InProgress]: "processing",
    [TaskStatus.Completed]: "success",
    [TaskStatus.Cancelled]: "error"
  };

  const statusLabels = {
    [TaskStatus.Pending]: "Ожидает",
    [TaskStatus.InProgress]: "В работе",
    [TaskStatus.Completed]: "Завершена",
    [TaskStatus.Cancelled]: "Отменена"
  };

  const priorityLabels = {
    [TaskPriority.Low]: "Низкий",
    [TaskPriority.Medium]: "Средний",
    [TaskPriority.High]: "Высокий",
    [TaskPriority.Urgent]: "Срочный"
  };

  // Загрузка задач
  const loadTasks = async () => {
    setLoading(true);
    try {
      const response = await taskApi.getTasks();
      let tasksData = response.tasks || [];
      
      if (tasksData && typeof tasksData === 'object' && !Array.isArray(tasksData)) {
        tasksData = Object.values(tasksData);
      }
      
      setTasks(tasksData);
    } catch (error) {
      console.error("Ошибка при загрузке задач:", error);
      message.error("Ошибка при загрузке задач");
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  // Загрузка пользователей
  const loadUsers = async () => {
    try {
      const response = await api.get("/user");
      setUsers(response.data.users);
    } catch (error) {
      console.error("Ошибка при загрузке пользователей:", error);
    }
  };

  useEffect(() => {
    loadTasks();
    loadUsers();
  }, []);

  const filteredTasks = tasks?.filter((task) => {
    if (selectedStatus !== "all" && task.status !== selectedStatus) return false;
    if (selectedPriority !== "all" && task.priority !== selectedPriority) return false;
    return true;
  }) || [];

  const groupedTasks = {
    [TaskStatus.Pending]: filteredTasks.filter((task) => task.status === TaskStatus.Pending),
    [TaskStatus.InProgress]: filteredTasks.filter((task) => task.status === TaskStatus.InProgress),
    [TaskStatus.Completed]: filteredTasks.filter((task) => task.status === TaskStatus.Completed),
    [TaskStatus.Cancelled]: filteredTasks.filter((task) => task.status === TaskStatus.Cancelled)
  };

  const handleCreateTask = async (values: any) => {
    try {
      const taskData: CreateTaskRequest = {
        ...values,
        dueDate: values.dueDate?.toISOString(),
        assignedTo: values.assignedTo || undefined
      };

      await taskApi.createTask(taskData);
      message.success("Задача создана успешно");
      setIsModalVisible(false);
      form.resetFields();
      loadTasks();
    } catch (error: any) {
      message.error(error.message || "Ошибка при создании задачи");
    }
  };

  const handleUpdateTask = async (taskId: string, values: any) => {
    try {
      const taskData: UpdateTaskRequest = {
        ...values,
        dueDate: values.dueDate?.toISOString()
      };

      await taskApi.updateTask(taskId, taskData);
      message.success("Задача обновлена успешно");
      setEditingTask(null);
      form.resetFields();
      loadTasks();
    } catch (error: any) {
      message.error(error.message || "Ошибка при обновлении задачи");
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      await taskApi.deleteTask(taskId);
      message.success("Задача удалена успешно");
      loadTasks();
    } catch (error: any) {
      message.error(error.message || "Ошибка при удалении задачи");
    }
  };

  const handleEditTask = (task: ITask) => {
    setEditingTask(task);
    form.setFieldsValue({
      title: task.title,
      description: task.description,
      priority: task.priority,
      status: task.status,
      dueDate: task.dueDate ? dayjs(task.dueDate) : undefined,
      assignedTo: task.assignedTo?._id,
      tags: task.tags
    });
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingTask(null);
    form.resetFields();
  };

  // Drag & Drop handlers
  const handleDragStart = (e: React.DragEvent, taskId: string) => {
    e.dataTransfer.setData("taskId", taskId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, targetStatus: TaskStatus) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData("taskId");
    
    if (!taskId) return;

    const task = tasks.find(t => t._id === taskId);
    if (!task) return;

    // Проверяем права на изменение статуса
    const isAssigned = task.assignedTo?._id === authState.user?._id;
    if (!isAdmin && !isAssigned) {
      message.error("Вы можете изменять статус только назначенных вам задач");
      return;
    }

    if (task.status === targetStatus) return;

    // Сразу обновляем локально для мгновенной обратной связи
    const updatedTask = { ...task, status: targetStatus };
    setTasks(prevTasks => 
      prevTasks.map(t => t._id === taskId ? updatedTask : t)
    );

    try {
      await taskApi.updateTask(taskId, { status: targetStatus });
      message.success("Статус задачи обновлен");
    } catch (error: any) {
      // Откатываем изменения при ошибке
      setTasks(prevTasks => 
        prevTasks.map(t => t._id === taskId ? task : t)
      );
      message.error(error.message || "Ошибка при обновлении статуса");
    }
  };

  // Открытие модального окна деталей
  const openTaskDetails = (task: ITask) => {
    setSelectedTask(task);
    setDetailsModalVisible(true);
  };

  // Локальное обновление задачи
  const handleTaskUpdate = (updatedTask: ITask) => {
    setTasks(prevTasks => 
      prevTasks.map(t => t._id === updatedTask._id ? updatedTask : t)
    );
    setSelectedTask(updatedTask);
  };

  const renderTaskCard = (task: ITask) => {
    const isOverdue = task.dueDate && dayjs(task.dueDate).isBefore(dayjs(), 'day') && task.status !== TaskStatus.Completed;
    const isDueToday = task.dueDate && dayjs(task.dueDate).isSame(dayjs(), 'day');

    return (
      <Card
        key={task._id}
        size="small"
        style={{
          marginBottom: 12,
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderLeft: `4px solid ${
            task.priority === TaskPriority.Urgent ? '#ff4d4f' :
            task.priority === TaskPriority.High ? '#ff7875' :
            task.priority === TaskPriority.Medium ? '#ffa940' : '#1890ff'
          }`,
          backgroundColor: isOverdue ? '#fff2f0' : '#ffffff',
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
        className="task-card"
        draggable
        onDragStart={(e) => handleDragStart(e, task._id)}
        onClick={() => openTaskDetails(task)}
        actions={[
          <Tooltip title="Просмотреть детали">
            <EyeOutlined style={{ color: '#1890ff' }} />
          </Tooltip>,
          <Tooltip title="Редактировать">
            <EditOutlined 
              style={{ color: '#52c41a' }} 
              onClick={(e) => {
                e.stopPropagation();
                handleEditTask(task);
              }}
            />
          </Tooltip>,
          <Tooltip title="Удалить">
            <Popconfirm
              title="Вы уверены, что хотите удалить эту задачу?"
              onConfirm={(e) => {
                e?.stopPropagation();
                handleDeleteTask(task._id);
              }}
              okText="Да"
              cancelText="Нет"
            >
              <DeleteOutlined 
                style={{ color: '#ff4d4f' }} 
                onClick={(e) => e.stopPropagation()}
              />
            </Popconfirm>
          </Tooltip>
        ]}
      >
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <Title level={5} style={{ margin: 0, flex: 1, color: '#262626' }}>
              {task.title}
            </Title>
            <Space>
              <Badge 
                status={statusColors[task.status] as any} 
                text={<span style={{ fontSize: '12px' }}>{statusLabels[task.status]}</span>} 
              />
              <Tag color={priorityColors[task.priority]} style={{ fontSize: '11px', fontWeight: 500 }}>
                {priorityLabels[task.priority]}
              </Tag>
            </Space>
          </div>

          {task.description && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: '13px', lineHeight: '1.4' }}>
              {task.description}
            </Text>
          )}

          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <UserOutlined style={{ color: '#8c8c8c' }} />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {task.assignedTo ? task.assignedTo.name : "Не назначен"}
                </Text>
              </Space>
              {task.dueDate && (
                <Space>
                  <CalendarOutlined style={{ color: isOverdue ? '#ff4d4f' : isDueToday ? '#faad14' : '#8c8c8c' }} />
                  <Text 
                    type={isOverdue ? "danger" : isDueToday ? "warning" : "secondary"}
                    style={{ fontSize: '12px', fontWeight: isOverdue ? 600 : 400 }}
                  >
                    {dayjs(task.dueDate).format("DD.MM.YYYY")}
                  </Text>
                </Space>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {task.tags.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', marginRight: 12 }}>
                    <TagOutlined style={{ marginRight: 4, color: '#8c8c8c', fontSize: '12px' }} />
                    {task.tags.slice(0, 2).map(tag => (
                      <Tag key={tag} style={{ fontSize: '10px', marginRight: 4 }}>
                        {tag}
                      </Tag>
                    ))}
                    {task.tags.length > 2 && (
                      <Text type="secondary" style={{ fontSize: '10px' }}>
                        +{task.tags.length - 2}
                      </Text>
                    )}
                  </div>
                )}
                
                {task.comments.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CommentOutlined style={{ marginRight: 4, color: '#8c8c8c', fontSize: '12px' }} />
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      {task.comments.length}
                    </Text>
                  </div>
                )}
              </div>
              
              {/* Кнопки действий */}
              <div style={{ display: 'flex', gap: '4px' }}>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditTask(task);
                  }}
                  style={{ 
                    padding: '2px 6px', 
                    height: '24px',
                    color: '#52c41a',
                    border: 'none'
                  }}
                />
                <Popconfirm
                  title="Удалить задачу?"
                  onConfirm={(e) => {
                    e?.stopPropagation();
                    handleDeleteTask(task._id);
                  }}
                  okText="Да"
                  cancelText="Нет"
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={(e) => e.stopPropagation()}
                    style={{ 
                      padding: '2px 6px', 
                      height: '24px',
                      color: '#ff4d4f',
                      border: 'none'
                    }}
                  />
                </Popconfirm>
              </div>
            </div>
          </Space>
        </div>
      </Card>
    );
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ 
        marginBottom: 24, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '16px 0',
        borderBottom: '2px solid #f0f0f0'
      }}>
        <div>
          <Title level={2} style={{ margin: 0, color: '#262626' }}>
            Задачи
          </Title>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            Управление задачами и проектами
          </Text>
        </div>
        <Space size="middle">
          <Button
            icon={<ReloadOutlined />}
            onClick={loadTasks}
            loading={loading}
            style={{ borderRadius: '6px' }}
          >
            Обновить
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsModalVisible(true)}
            style={{ borderRadius: '6px', boxShadow: '0 2px 4px rgba(24, 144, 255, 0.3)' }}
          >
            Создать задачу
          </Button>
        </Space>
      </div>

      <Card 
        style={{ 
          marginBottom: 24,
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}
        bodyStyle={{ padding: '20px' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
          <FilterOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
          <Text strong style={{ fontSize: '16px' }}>Фильтры</Text>
        </div>
        <Row gutter={16}>
          <Col span={8}>
            <div style={{ marginBottom: '8px' }}>
              <Text type="secondary">Статус</Text>
            </div>
            <Select
              placeholder="Выберите статус"
              value={selectedStatus}
              onChange={setSelectedStatus}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="all">Все статусы</Option>
              {Object.entries(statusLabels).map(([status, label]) => (
                <Option key={status} value={status}>{label}</Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <div style={{ marginBottom: '8px' }}>
              <Text type="secondary">Приоритет</Text>
            </div>
            <Select
              placeholder="Выберите приоритет"
              value={selectedPriority}
              onChange={setSelectedPriority}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="all">Все приоритеты</Option>
              {Object.entries(priorityLabels).map(([priority, label]) => (
                <Option key={priority} value={priority}>{label}</Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <div style={{ marginBottom: '8px' }}>
              <Text type="secondary">Действия</Text>
            </div>
            <Button 
              onClick={() => {
                setSelectedStatus("all");
                setSelectedPriority("all");
              }}
              style={{ width: '100%' }}
            >
              Сбросить фильтры
            </Button>
          </Col>
        </Row>
      </Card>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Загружаем задачи...</Text>
          </div>
        </div>
      ) : (
        <Row gutter={16}>
          {Object.entries(groupedTasks).map(([status, tasks]) => (
            <Col span={6} key={status}>
              <Card
                title={
                  <Space>
                    <Badge count={tasks.length} style={{ backgroundColor: statusColors[status as TaskStatus] }} />
                    <span style={{ fontWeight: 600, color: '#262626' }}>
                      {statusLabels[status as TaskStatus]}
                    </span>
                  </Space>
                }
                style={{ 
                  height: 'fit-content',
                  minHeight: '400px',
                  background: 'linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%)'
                }}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, status as TaskStatus)}
                headStyle={{
                  borderBottom: '2px solid #e8e8e8',
                  backgroundColor: '#fafafa'
                }}
                bodyStyle={{
                  padding: '16px',
                  minHeight: '300px'
                }}
              >
                {tasks.length === 0 ? (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '200px',
                    color: '#8c8c8c'
                  }}>
                    <Empty 
                      description="Нет задач" 
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </div>
                ) : (
                  <div style={{ 
                    maxHeight: '70vh', 
                    overflowY: 'auto',
                    paddingRight: '4px'
                  }}>
                    {tasks.map(renderTaskCard)}
                  </div>
                )}
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Модальное окно создания/редактирования задачи */}
      <Modal
        title={editingTask ? "Редактировать задачу" : "Создать задачу"}
        open={isModalVisible}
        onCancel={handleModalCancel}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={editingTask ? (values) => handleUpdateTask(editingTask._id, values) : handleCreateTask}
        >
          <Form.Item
            name="title"
            label="Название"
            rules={[{ required: true, message: "Введите название задачи" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="description" label="Описание">
            <TextArea rows={3} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="priority" label="Приоритет" initialValue={TaskPriority.Medium}>
                <Select>
                  {Object.entries(priorityLabels).map(([priority, label]) => (
                    <Option key={priority} value={priority}>{label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Статус" initialValue={TaskStatus.Pending}>
                <Select>
                  {Object.entries(statusLabels).map(([status, label]) => (
                    <Option key={status} value={status}>{label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="dueDate" label="Срок выполнения">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="assignedTo" label="Назначить">
                <Select
                  placeholder="Выберите пользователя"
                  allowClear
                  disabled={!isAdmin}
                >
                  {users.map((user) => (
                    <Option key={user._id} value={user._id}>
                      {user.name} ({user.username})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="tags" label="Теги">
            <Select mode="tags" placeholder="Добавьте теги" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingTask ? "Обновить" : "Создать"}
              </Button>
              <Button onClick={handleModalCancel}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно деталей задачи */}
      <TaskDetailsModal
        task={selectedTask}
        visible={detailsModalVisible}
        onClose={() => setDetailsModalVisible(false)}
        onUpdate={loadTasks}
        onTaskUpdate={handleTaskUpdate}
      />
    </div>
  );
};

export default TasksPage; 